/**
 * VÉRAULT — Essentials · Activities shelf. Prices out the still-unbooked /
 * optional activities on the itinerary (items carrying an `estimate`).
 * Each activity has a "Purchased" toggle — marking one purchased folds its
 * cost into the Trip Budget totals above (remaining drops, paid rises);
 * un-marking restores it. Shared across both travelers. Self-silent when empty.
 */
import React from "react";
import { Compass, ExternalLink, Check, Plus, Trash2, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../../../index";
import { usePreviewBase } from "../../../../contexts/PreviewContext";

const EUR_TO_USD = 1.08; // approximate — for client estimate display only
const money = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(Number(n || 0));
function toUSD(text) {
  if (!text) return text;
  return text.replace(/€\s?(\d+(?:\.\d+)?)(?:\s?[–-]\s?(\d+(?:\.\d+)?))?/g, (_m, a, b) => {
    const A = Math.round(parseFloat(a) * EUR_TO_USD);
    return b ? `$${A}–${Math.round(parseFloat(b) * EUR_TO_USD)}` : `$${A}`;
  });
}

export default function ActivitiesShelf({ tripId, budget, onLoaded, onReservations }) {
  const { isPreview } = usePreviewBase();
  const [rows, setRows] = React.useState(null);
  const [usd, setUsd] = React.useState(false);
  const [purchased, setPurchased] = React.useState(() => new Set());
  const [busy, setBusy] = React.useState(null);
  const [spaVal, setSpaVal] = React.useState(null); // null = untouched, "" = cleared
  const [addOpen, setAddOpen] = React.useState(false);
  const [add, setAdd] = React.useState({ label: "", amount: "", date: "" });

  const spaEntry = (budget?.your_spending?.entries || []).find((e) => e.type === "spa");
  const spaAmount = spaEntry?.amount || 0;
  const cur = budget?.currency || "USD";
  const ys = budget?.your_spending;

  const saveSpa = async () => {
    if (isPreview || busy === "spa") return;
    const raw = (spaVal ?? "").trim();
    const amount = raw === "" ? 0 : (parseFloat(raw) || 0);
    setBusy("spa");
    try {
      await api.post(`/client-portal/trips/${tripId}/budget/entries`, { type: "spa", amount, label: "Serenity Spa" });
      setSpaVal(null);
      window.dispatchEvent(new CustomEvent("vt:sync-changed:essentials"));
      toast.success(amount > 0 ? `Serenity Spa set — ${money(amount)}` : "Serenity Spa cleared");
    } catch (e) {
      toast.error("Couldn't save — please try again");
    } finally {
      setBusy(null);
    }
  };

  React.useEffect(() => {
    if (!tripId) return undefined;
    let alive = true;
    api.get(`/client-portal/trips/${tripId}/design-studio-itinerary`)
      .then(({ data }) => {
        if (!alive) return;
        const list = [];
        for (const d of data.days || []) {
          for (const it of d.items || []) {
            if (it.estimate) {
              list.push({
                id: it.id, title: it.title, estimate: it.estimate, status: it.estimate_status,
                date: d.date, link: it.website || it.booking_link || it.map_url,
                price_usd: typeof it.purchase_price_usd === "number" ? it.purchase_price_usd : 0,
              });
            }
          }
        }
        setRows(list);
        onLoaded && onLoaded(list.length > 0);
        onReservations && onReservations(list.some((r) => /furnas|pateo|vicente/i.test(r.title || "")));
      })
      .catch(() => { setRows([]); onLoaded && onLoaded(false); });
    // purchased state (shared per trip)
    api.get(`/client-portal/trips/${tripId}/activity-purchases`)
      .then(({ data }) => { if (alive) setPurchased(new Set(data.purchased_ids || [])); })
      .catch(() => {});
    return () => { alive = false; };
  }, [tripId, onLoaded]);

  const togglePurchase = async (row) => {
    if (isPreview || busy) return;
    const willPurchase = !purchased.has(row.id);
    setBusy(row.id);
    // optimistic
    setPurchased((prev) => {
      const next = new Set(prev);
      if (willPurchase) next.add(row.id); else next.delete(row.id);
      return next;
    });
    try {
      await api.post(`/client-portal/trips/${tripId}/activity-purchases/toggle`, {
        item_id: row.id, purchased: willPurchase,
      });
      // nudge the Essentials aggregator so the Trip Budget totals move live
      window.dispatchEvent(new CustomEvent("vt:sync-changed:essentials"));
      if (row.price_usd > 0) {
        toast.success(willPurchase
          ? `Added to Your Spending — $${row.price_usd.toFixed(2)}`
          : `Removed from Your Spending — $${row.price_usd.toFixed(2)}`);
      } else {
        toast.success(willPurchase ? "Marked as purchased" : "Marked as not purchased");
      }
    } catch (e) {
      // revert
      setPurchased((prev) => {
        const next = new Set(prev);
        if (willPurchase) next.delete(row.id); else next.add(row.id);
        return next;
      });
      toast.error("Couldn't update that just now. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  if (!rows || rows.length === 0) return null;

  const RESO = /furnas|pateo|vicente/i;
  const reservationRows = rows.filter((r) => RESO.test(r.title || ""));
  const estimateRows = rows.filter((r) => !RESO.test(r.title || "") && !/serenity spa/i.test(r.title || ""));
  const spaRows = rows.filter((r) => /serenity spa/i.test(r.title || ""));
  const addedActivities = (budget?.your_spending?.entries || []).filter((e) => e.type === "activity");

  const refresh = () => window.dispatchEvent(new CustomEvent("vt:sync-changed:essentials"));
  const saveActivity = async () => {
    if (isPreview || !add.label.trim()) { setAddOpen(false); return; }
    setBusy("add");
    try {
      await api.post(`/client-portal/trips/${tripId}/budget/entries`, {
        type: "activity", label: add.label, amount: parseFloat(add.amount || 0) || 0, date: add.date });
      setAdd({ label: "", amount: "", date: "" }); setAddOpen(false); refresh();
      toast.success("Added to your activities");
    } catch (e) { toast.error("Couldn't add — please try again"); } finally { setBusy(null); }
  };
  const deleteActivity = async (id) => {
    if (isPreview) return;
    try { await api.delete(`/client-portal/trips/${tripId}/budget/entries/${id}`); refresh(); toast.success("Removed"); }
    catch (e) { toast.error("Couldn't remove"); }
  };

  return (
    <>
      {reservationRows.length > 0 && (
        <section id="restaurants" className="space-y-2 mb-6 scroll-mt-24" data-testid="restaurant-reservations">
          <div className="flex items-center gap-2">
            <CalendarCheck className="size-4 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
            <h2 className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))]">Restaurant Reservations</h2>
          </div>
          <ul className="rounded-md border border-[rgba(0,0,0,0.06)] bg-white/50 divide-y divide-[rgba(0,0,0,0.05)]">
            {reservationRows.map((r, i) => (
              <li key={r.id || i} className="p-3.5 flex items-start justify-between gap-3" data-testid={`reservation-row-${i}`}>
                <div className="min-w-0">
                  <p className="font-serif text-[14.5px] leading-snug">{r.title}</p>
                  <div className="mt-0.5 flex items-center gap-2 flex-wrap">
                    {r.date && <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-[hsl(var(--foreground-muted))]">{new Date(r.date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>}
                    {r.link && (
                      <a href={r.link} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.14em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))]">
                        <ExternalLink className="size-2.5" strokeWidth={1.5} /> Details
                      </a>
                    )}
                  </div>
                </div>
                <span className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.16em] text-[hsl(var(--accent-gold))] whitespace-nowrap shrink-0">
                  <Check className="size-3" strokeWidth={2.2} /> Reservation confirmed
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    <section id="activities" className="space-y-3" data-testid="activities-shelf">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Compass className="size-4 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
          <h2 className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))]">Activities · estimates</h2>
          {!isPreview && (
            <button type="button" onClick={() => setAddOpen((v) => !v)} data-testid="activity-add-toggle"
              className="ml-1 inline-flex items-center gap-1 rounded-full border border-[rgba(0,0,0,0.14)] px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.14em] text-[hsl(var(--foreground-muted))] hover:border-[hsl(var(--accent-gold))] hover:text-[hsl(var(--accent-gold))] transition-colors">
              <Plus className="size-2.5" strokeWidth={2} /> Add new activity
            </button>
          )}
        </div>
        <div className="inline-flex rounded-full border border-[rgba(0,0,0,0.1)] overflow-hidden" data-testid="activities-currency-toggle">
          <button type="button" onClick={() => setUsd(false)} data-testid="activities-currency-eur"
            className={`px-2.5 py-1 font-mono text-[9.5px] uppercase tracking-[0.16em] transition-colors ${!usd ? "bg-[hsl(var(--accent-gold))]/15 text-[hsl(var(--accent-gold))]" : "text-[hsl(var(--foreground-muted))]"}`}>€ EUR</button>
          <button type="button" onClick={() => setUsd(true)} data-testid="activities-currency-usd"
            className={`px-2.5 py-1 font-mono text-[9.5px] uppercase tracking-[0.16em] transition-colors ${usd ? "bg-[hsl(var(--accent-gold))]/15 text-[hsl(var(--accent-gold))]" : "text-[hsl(var(--foreground-muted))]"}`}>$ USD</button>
        </div>
      </div>
      <p className="font-accent italic text-[13px] text-[hsl(var(--foreground-secondary))]">
        Experiences still open on your itinerary. Mark one <span className="not-italic font-mono text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--accent-gold))]">Purchased</span> to add it to Your Spending. Un-mark it to remove it.
      </p>
      {ys && (
        <div className="flex justify-end">
          <div className="grid grid-cols-2 gap-2.5 w-full sm:w-auto sm:min-w-[340px]">
            <div className="rounded-lg border border-[rgba(0,0,0,0.08)] bg-white/60 px-3.5 py-2.5" data-testid="your-spending-card">
              <p className="font-mono text-[8px] uppercase tracking-[0.16em] text-[hsl(var(--foreground-muted))]">Your Spending</p>
              <p className="font-serif text-[18px] mt-0.5">{money(ys.total)}</p>
            </div>
            <div className="rounded-lg border border-[hsl(var(--accent-gold))]/40 bg-[hsl(var(--accent-gold))]/[0.05] px-3.5 py-2.5" data-testid="all-in-card">
              <p className="font-mono text-[8px] uppercase tracking-[0.16em] text-[hsl(var(--foreground-muted))]">All-In Trip Spend</p>
              <p className="font-serif text-[18px] mt-0.5">{money(budget.all_in_total)}</p>
            </div>
          </div>
        </div>
      )}
      {addOpen && !isPreview && (
        <div className="rounded-md border border-[hsl(var(--accent-gold))]/30 bg-[hsl(var(--accent-gold))]/[0.04] p-3 grid grid-cols-1 sm:grid-cols-4 gap-2 items-center" data-testid="activity-add-form">
          <input value={add.label} onChange={(e) => setAdd((p) => ({ ...p, label: e.target.value }))} placeholder="Activity name" data-testid="activity-add-label" className="sm:col-span-2 rounded border border-[rgba(0,0,0,0.12)] bg-white px-2.5 py-1.5 font-serif text-[13px]" />
          <input value={add.amount} onChange={(e) => setAdd((p) => ({ ...p, amount: e.target.value }))} type="number" step="0.01" placeholder="$0.00" data-testid="activity-add-amount" className="rounded border border-[rgba(0,0,0,0.12)] bg-white px-2.5 py-1.5 font-serif text-[13px]" />
          <button type="button" onClick={saveActivity} disabled={busy === "add"} data-testid="activity-add-save" className="rounded-full bg-[hsl(var(--accent-gold))] text-white px-3 py-1.5 font-mono text-[9.5px] uppercase tracking-[0.16em] disabled:opacity-50">Add</button>
        </div>
      )}
      <ul className="rounded-md border border-[rgba(0,0,0,0.06)] bg-white/50 divide-y divide-[rgba(0,0,0,0.05)]">
        {[...estimateRows, ...spaRows].map((r, i) => {
          const isBought = purchased.has(r.id);
          const isSpa = /serenity spa/i.test(r.title || "");
          return (
            <li key={r.id || i} className={`p-3.5 flex items-start justify-between gap-3 transition-colors ${!isSpa && isBought ? "bg-[hsl(var(--accent-gold))]/[0.06]" : ""}`} data-testid={`activity-row-${i}`}>
              <div className="min-w-0">
                <p className="font-serif text-[14.5px] leading-snug">{r.title}</p>
                <div className="mt-0.5 flex items-center gap-2 flex-wrap">
                  {r.date && <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-[hsl(var(--foreground-muted))]">{new Date(r.date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>}
                  {r.status && !isSpa && <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-[hsl(var(--accent-gold))]">{r.status}</span>}
                  {isSpa && <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-[hsl(var(--accent-gold))]">To Be Booked by Client</span>}
                  {r.link && (
                    <a href={r.link} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.14em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))]">
                      <ExternalLink className="size-2.5" strokeWidth={1.5} /> Details
                    </a>
                  )}
                </div>
                {isSpa ? (
                  <p className="font-accent italic text-[11.5px] text-[hsl(var(--foreground-muted))] mt-1.5">Enter what you spend here.</p>
                ) : (
                  <button
                    type="button"
                    onClick={() => togglePurchase(r)}
                    disabled={isPreview || busy === r.id}
                    data-testid={`activity-purchase-toggle-${i}`}
                    className={`mt-2 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.18em] transition-colors disabled:opacity-50 ${
                      isBought
                        ? "border-[hsl(var(--accent-gold))] bg-[hsl(var(--accent-gold))]/15 text-[hsl(var(--foreground))]"
                        : "border-[rgba(0,0,0,0.14)] text-[hsl(var(--foreground-secondary))] hover:border-[hsl(var(--accent-gold))] hover:text-[hsl(var(--accent-gold))]"
                    }`}
                  >
                    {isBought ? <Check className="size-2.5" strokeWidth={2.2} /> : null}
                    {isBought ? "Purchased" : "Mark purchased"}
                  </button>
                )}
              </div>
              {isSpa ? (
                isPreview ? (
                  <span className="font-serif text-[14px] whitespace-nowrap shrink-0" data-testid={`activity-price-${i}`}>{spaAmount > 0 ? money(spaAmount) : "$0.00"}</span>
                ) : (
                  <div className="flex items-center gap-2 shrink-0">
                    <input type="number" step="0.01" min="0" data-testid="spa-amount-input" placeholder="$0.00"
                      value={spaVal !== null ? spaVal : (spaAmount > 0 ? String(spaAmount) : "")}
                      onChange={(e) => setSpaVal(e.target.value)}
                      className="w-24 rounded border border-[rgba(0,0,0,0.14)] bg-white px-2.5 py-1.5 font-serif text-[13px] text-right" />
                    <button type="button" onClick={saveSpa} disabled={busy === "spa"} data-testid="spa-save" className="rounded-full bg-[hsl(var(--accent-gold))] text-white size-7 grid place-items-center disabled:opacity-50"><Check className="size-3.5" strokeWidth={2} /></button>
                  </div>
                )
              ) : (
                <span className={`font-serif text-[14px] whitespace-nowrap shrink-0 ${isBought ? "text-[hsl(var(--foreground-muted))] line-through" : "text-[hsl(var(--foreground-secondary))]"}`} data-testid={`activity-price-${i}`}>{usd ? toUSD(r.estimate) : r.estimate}</span>
              )}
            </li>
          );
        })}
        {addedActivities.map((e) => (
          <li key={e.id} className="p-3.5 flex items-start justify-between gap-3" data-testid={`added-activity-${e.id}`}>
            <div className="min-w-0">
              <p className="font-serif text-[14.5px] leading-snug">{e.label}</p>
              <div className="mt-0.5 flex items-center gap-2 flex-wrap">
                {e.date && <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-[hsl(var(--foreground-muted))]">{e.date}</span>}
                <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-[hsl(var(--accent-gold))]">Added by Client</span>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="font-serif text-[14px]">{money(e.amount)}</span>
              {!isPreview && (
                <button type="button" onClick={() => deleteActivity(e.id)} data-testid={`added-activity-delete-${e.id}`} className="size-7 grid place-items-center text-[hsl(var(--foreground-muted))] hover:text-red-600"><Trash2 className="size-3.5" strokeWidth={1.5} /></button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
    </>
  );
}
